/* eslint-disable react-hooks/exhaustive-deps */

import { Box } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';

import { showToast } from 'modules/layout/layout.actions';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import {
  getEmailTemplate,
  getEmailTemplateSummaries,
  getEmailTemplateTokens,
  putEmailTemplate,
  putEmailTemplateSetActive,
  putEmailTemplateSetCancelled,
  setTemplateId
} from 'modules/emailTemplates/store/emailTemplate.actions';
import {
  GET_EMAIL_TEMPLATE_FAILURE,
  GET_EMAIL_TEMPLATES_SUMMARIES_FAILURE,
  PUT_EMAIL_TEMPLATE_FAILURE
} from 'modules/emailTemplates/store/emailTemplateTypes';
import AdminEmailTemplateDetails from './adminEmailTemplateDetails.component';
import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { EMAIL_TEMPLATE_STATUS_IDS, ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const styles = theme => ({
  wideControl: {
    minWidth: 174
  },
  section: {
    marginBottom: theme.spacing(2)
  },
  selfServicePolicySelect: {
    minWidth: 300
  }
});

const AdminEmailTemplateDetailsView = props => {
  const templateId = props.match.params.templateId;

  const [isLoading, setIsLoading] = useState(false);
  const [template, setTemplate] = useState(null);

  const summaries = props.templates?.emailTemplates ?? [];
  const rootId = template?.parentEmailTemplateId ?? template?.templateId;

  const versionOptions = summaries
    .filter(s => (s.parentEmailTemplateId ?? s.templateId) === rootId)
    .slice()
    .sort((a, b) => (b.versionNumber ?? 1) - (a.versionNumber ?? 1));

  const isReadOnly =
    isIdEqual(template?.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.CANCELLED) ||
    isIdEqual(template?.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.EXPIRED) ||
    (isIdEqual(templateId?.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE) &&
      versionOptions?.find(s => s && s?.versionNumber === template?.versionNumber + 1));

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      props.setTemplateId(templateId);
      try {
        await fetchEnumerations(props);
        if (!props.templates || props.templates.areTokenLoaded === false) {
          await props.getEmailTemplateTokens();
        }
        if (!props.templates || props.templates.isLoaded === false) {
          await props.getEmailTemplateSummaries();
        }

        const fetchedTemplate = await props.getEmailTemplate(templateId);
        setTemplate(fetchedTemplate.response);
      } finally {
        setIsLoading(false);
      }
    }

    setTemplate(null);
    fetchData();
  }, [templateId]);

  const updateTemplate = async values => {
    setIsLoading(true);
    try {
      const resp = await props.putEmailTemplate(templateId, values);
      await props.getEmailTemplateSummaries();
      const fetchedTemplate = await props.getEmailTemplate(templateId);
      setTemplate(fetchedTemplate.response);
      props.history.push(`/templates/${resp.response.templateId}`);
      props.showToast('Template updated successfully', 'success');
    } catch (error) {
      props.showToast('Error updating template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async () => {
    setIsLoading(true);
    try {
      const templateResponse = await props.getEmailTemplate(templateId);
      if (templateResponse.type === GET_EMAIL_TEMPLATE_FAILURE || !templateResponse.response) {
        throw new Error('Failed to load template.');
      }

      const updateResponse = await props.putEmailTemplate(templateId, { ...templateResponse.response, showTemplate: false });
      if (updateResponse.type === PUT_EMAIL_TEMPLATE_FAILURE) {
        throw new Error('Failed to hide template.');
      }

      const summariesResponse = await props.getEmailTemplateSummaries();
      if (summariesResponse.type === GET_EMAIL_TEMPLATES_SUMMARIES_FAILURE) {
        throw new Error('Failed to refresh templates.');
      }

      props.showToast('Template hidden successfully', 'success');
      props.history.push('/templates');
    } catch (error) {
      props.showToast('Error hiding template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const changeStatus = async values => {
    setIsLoading(true);
    try {
      if (isIdEqual(values.versionStatusId, ZERO_GUID)) values.versionStatusId = EMAIL_TEMPLATE_STATUS_IDS.DRAFT;
      const resp =
        isIdEqual(values.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.DRAFT) ||
        isIdEqual(values.versionStatusId, ZERO_GUID) ||
        isIdEqual(values.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.EXPIRED)
          ? await props.putEmailTemplateSetActive(templateId, values)
          : await props.putEmailTemplateSetCancelled(templateId, values);

      await props.getEmailTemplateSummaries();
      const fetchedTemplate = await props.getEmailTemplate(templateId);
      setTemplate(fetchedTemplate.response);
      props.history.push(`/templates/${resp.response.templateId}`);
      props.showToast('Template updated successfully', 'success');
    } catch (error) {
      props.showToast('Error updating template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const canReadTemplates = hasPermission(Permissions.Admin.Templates.Read);
  const canEditTemplates = hasPermission(Permissions.Admin.Templates.Edit);

  if (!canReadTemplates) {
    props.history.push('/');
    return null;
  }

  return (
    <Box>
      {!isLoading && template && (
        <AdminEmailTemplateDetails
          enumerations={props.enumerations ? props.enumerations.allEnums : []}
          template={template}
          updateTemplate={updateTemplate}
          deleteTemplate={deleteTemplate}
          tokens={props.templates ? props.templates.tokens : []}
          changeStatus={changeStatus}
          versionOptions={versionOptions}
          history={props.history}
          key={templateId}
          isReadOnly={isReadOnly || !canEditTemplates}
        />
      )}
      {isLoading && <FullscreenSpinner />}
    </Box>
  );
};

const mapStateToProps = state => {
  const enumerations = state.enumerations;
  const templates = state.templates;
  return {
    enumerations,
    templates
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    showToast,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getEmailTemplateSummaries,
    getEmailTemplate,
    putEmailTemplate,
    getEmailTemplateTokens,
    putEmailTemplateSetActive,
    putEmailTemplateSetCancelled,
    setTemplateId
  })
)(AdminEmailTemplateDetailsView);
